import type { Contact } from "@/features/contact/types";
import type {
  ContactFilterQuery,
  ContactFilterRule,
  ConversationFilterFieldName,
} from "@/features/inbox/types/filter";

import { isFollowUpDueByEndOfToday } from "@/features/contact/helpers/follow-up";
import {
  hasPendingDraft,
  pendingApprovalCount,
  pendingQuestionCount,
} from "@/features/contact/helpers/ai-pending";
import { needsYou } from "@/features/contact/helpers/needs-you";
import { isParkedFromDefaultView } from "@/features/follow-up/helpers/park";
import { ASSIGNED_TO_TEAMMATES } from "@/features/inbox/helpers/inbox-filter-url";
import { type InboxFilter } from "@/features/inbox/stores/inbox";
import { CONVERSATION_VIEW } from "@/types/conversation";
import { OWNER } from "@/types/message";

/**
 * Tri-state because the client can only evaluate filter rules whose data lives
 * on the contact object. "match"/"no-match" are confident verdicts; everything
 * we can't derive (channel, segment, …) is "indeterminate" so the reconciler
 * never wrongly drops or adds a row - the server settles it on the next fetch.
 */
export type MatchResult = "match" | "no-match" | "indeterminate";

type FieldKind = "text" | "id" | "ids" | "date" | "boolean";

interface FieldDef {
  kind: FieldKind;
  get: (contact: Contact) => string | string[] | boolean | null;
}

// Query-builder field name → how to read it off the contact. Fields NOT listed
// here (lastInteractedChannel, broadcast, segment, form, utm*, advertisement)
// aren't derivable from the conversation object, so any rule on them is
// indeterminate. `advertisement` in particular is intentionally absent: the
// conversation-list contact payload never carries `adIds` (the server filters
// ads via a join but doesn't project the ids back), so evaluating it here would
// read an always-empty array and wrongly drop ad-filtered rows on the next cache
// reconcile - e.g. clicking a conversation would hide it from the list.
// `channel` IS derivable (unlike ads): the list query joins contact_channels, so
// each row carries the full contact.channels, and the filter's value is exactly
// the set of channel ids. Deriving it lets the reconciler add/drop a row on
// new-contact / channel-update events instead of leaving it to a hard refetch.
// (`lastInteractedChannel` stays out - it filters on channel *type* of the single
// most-recent channel, which the list payload doesn't distinguish.)
const FIELD_DEFS: Partial<Record<ConversationFilterFieldName, FieldDef>> = {
  firstName: { kind: "text", get: (c) => c.firstName },
  lastName: { kind: "text", get: (c) => c.lastName },
  email: { kind: "text", get: (c) => c.email },
  phone: { kind: "text", get: (c) => c.phone },
  country: { kind: "id", get: (c) => c.country },
  language: { kind: "id", get: (c) => c.language },
  assignee: { kind: "id", get: (c) => c.assignedUserId },
  team: { kind: "id", get: (c) => c.assignedTeamId },
  status: { kind: "id", get: (c) => c.status },
  lifecycle: { kind: "id", get: (c) => c.lifecycleId },
  origin: { kind: "id", get: (c) => c.origin },
  channel: { kind: "ids", get: (c) => c.channels.map((ch) => ch.id) },
  tags: { kind: "ids", get: (c) => c.tags },
  treatment: { kind: "ids", get: (c) => c.treatmentIds },
  product: { kind: "ids", get: (c) => c.productIds },
  website: { kind: "ids", get: (c) => c.websiteIds },
  createdAt: { kind: "date", get: (c) => c.createdAt },
  lastInteractedAt: { kind: "date", get: (c) => c.lastBumpedAt },
  // The AI attention flags, so clearing one drops the row out of a "Needs
  // human" / "Draft ready" list right away instead of on the next refetch.
  needsHuman: { kind: "boolean", get: (c) => c.humanAgentRequired },
  knowledgeGap: { kind: "boolean", get: (c) => c.knowledgeGapPending },
  draftPending: { kind: "boolean", get: hasPendingDraft },
  questionPending: {
    kind: "boolean",
    get: (c) => pendingQuestionCount(c) > 0,
  },
  approvalPending: {
    kind: "boolean",
    get: (c) => pendingApprovalCount(c) > 0,
  },
  needsYou: { kind: "boolean", get: needsYou },
  // The Filters drawer's follow-up facet. Both are on the conversation payload,
  // so marking one done drops the row out of a follow-up-filtered list right
  // away instead of on the next refetch.
  followUpRequired: { kind: "boolean", get: (c) => c.followUp != null },
  followUpDue: {
    kind: "boolean",
    get: (c) => isFollowUpDueByEndOfToday(c.followUp?.dueAt),
  },
};

/**
 * True when any rule names a field FIELD_DEFS can't derive. Every event against
 * such a query is indeterminate, so the reconciler can never add or drop a row
 * on its own. Ad-hoc filters tolerated that because they were transient and
 * refetched constantly; a custom view is someone's furniture, so callers use
 * this to refetch on realtime events instead of reconciling into a list that
 * would otherwise never change.
 */
export const hasNonDerivableRule = (
  query: ContactFilterQuery | null | undefined,
): boolean =>
  (query?.rules ?? []).some(
    (item) =>
      typeof item !== "string" &&
      !("rules" in item) &&
      !FIELD_DEFS[
        (item as ContactFilterRule).field as ConversationFilterFieldName
      ],
  );

const asString = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

const asStringArray = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item) => typeof item === "string")
    ? (value as string[])
    : null;

// Each evaluator returns null for an operator it doesn't support → indeterminate.
const evalText = (
  value: string | null,
  operator: string,
  raw: unknown,
): boolean | null => {
  const haystack = (value ?? "").toLowerCase();
  const needle = (asString(raw) ?? "").toLowerCase();

  switch (operator) {
    case "contains":
      return haystack.includes(needle);
    case "=":
      return haystack === needle;
    case "beginsWith":
      return haystack.startsWith(needle);
    case "doesNotBeginWith":
      return !haystack.startsWith(needle);
    case "endsWith":
      return haystack.endsWith(needle);
    case "null":
      return (value ?? "") === "";
    case "notNull":
      return (value ?? "") !== "";
    default:
      return null;
  }
};

const evalId = (
  value: string | null,
  operator: string,
  raw: unknown,
): boolean | null => {
  switch (operator) {
    case "isEqualTo":
      return value === asString(raw);
    case "isNotEqualTo":
      return value !== asString(raw);
    case "hasAnyOf": {
      const set = asStringArray(raw);

      return set === null ? null : value != null && set.includes(value);
    }
    case "hasNoneOf": {
      const set = asStringArray(raw);

      return set === null ? null : value == null || !set.includes(value);
    }
    case "exists":
      return value != null && value !== "";
    case "doesNotExist":
      return value == null || value === "";
    default:
      return null;
  }
};

const evalIds = (
  value: string[],
  operator: string,
  raw: unknown,
): boolean | null => {
  switch (operator) {
    case "hasAnyOf": {
      const set = asStringArray(raw);

      return set === null ? null : value.some((item) => set.includes(item));
    }
    case "hasNoneOf": {
      const set = asStringArray(raw);

      return set === null ? null : !value.some((item) => set.includes(item));
    }
    case "isEqualTo": {
      // Equality on a multi-value field is membership (mirrors the backend).
      const target = asString(raw);

      return target === null ? null : value.includes(target);
    }
    case "isNotEqualTo": {
      const target = asString(raw);

      return target === null ? null : !value.includes(target);
    }
    case "exists":
      return value.length > 0;
    case "doesNotExist":
      return value.length === 0;
    default:
      return null;
  }
};

const evalDate = (
  value: string | null,
  operator: string,
  raw: unknown,
): boolean | null => {
  if (operator === "exists") return Boolean(value);
  if (operator === "doesNotExist") return !value;
  if (!value) return null;
  const time = Date.parse(value);

  if (Number.isNaN(time)) return null;

  switch (operator) {
    case "before": {
      const other = Date.parse(asString(raw) ?? "");

      return Number.isNaN(other) ? null : time < other;
    }
    case "after": {
      const other = Date.parse(asString(raw) ?? "");

      return Number.isNaN(other) ? null : time > other;
    }
    case "between": {
      // The query builder serializes a date range as { start, end } (matching
      // the backend), not a 2-element array.
      const range =
        raw && typeof raw === "object" && !Array.isArray(raw)
          ? (raw as { start?: unknown; end?: unknown })
          : null;
      const startStr = asString(range?.start);
      const endStr = asString(range?.end);

      if (!startStr || !endStr) return null;
      const start = Date.parse(startStr);
      const end = Date.parse(endStr);

      return Number.isNaN(start) || Number.isNaN(end)
        ? null
        : time >= start && time <= end;
    }
    // Relative operators (inTheLast / moreThanAgo) are intentionally left
    // indeterminate - let the server reconcile rather than guess the encoding.
    default:
      return null;
  }
};

// Quick filters only ever assert `is true` (upsertBooleanRule drops the rule
// when toggled off), but honour an explicit `false` too rather than guessing.
const evalBoolean = (
  value: boolean,
  operator: string,
  expected: unknown,
): boolean | null => {
  if (operator !== "isEqualTo" || typeof expected !== "boolean") return null;

  return value === expected;
};

const toResult = (value: boolean | null): MatchResult =>
  value === null ? "indeterminate" : value ? "match" : "no-match";

const evaluateRule = (
  contact: Contact,
  rule: { field: string; operator: string; value: unknown },
): MatchResult => {
  const def = FIELD_DEFS[rule.field as ConversationFilterFieldName];

  if (!def) return "indeterminate";

  const value = def.get(contact);

  switch (def.kind) {
    case "text":
      return toResult(
        evalText(value as string | null, rule.operator, rule.value),
      );
    case "id":
      return toResult(
        evalId(value as string | null, rule.operator, rule.value),
      );
    case "ids":
      return toResult(
        evalIds((value as string[]) ?? [], rule.operator, rule.value),
      );
    case "date":
      return toResult(
        evalDate(value as string | null, rule.operator, rule.value),
      );
    case "boolean":
      return toResult(evalBoolean(Boolean(value), rule.operator, rule.value));
  }
};

// Kleene three-valued logic: no-match=false, match=true, indeterminate=unknown.
const andResult = (a: MatchResult, b: MatchResult): MatchResult =>
  a === "no-match" || b === "no-match"
    ? "no-match"
    : a === "indeterminate" || b === "indeterminate"
      ? "indeterminate"
      : "match";

const orResult = (a: MatchResult, b: MatchResult): MatchResult =>
  a === "match" || b === "match"
    ? "match"
    : a === "indeterminate" || b === "indeterminate"
      ? "indeterminate"
      : "no-match";

/**
 * Tri-state evaluation of the advanced filter group against a contact. Walks the
 * interleaved [rule, combinator, rule, …] list directly and mirrors the
 * backend's precedence: AND binds tighter than OR, so it splits into OR-groups,
 * ANDs the rules within each group, then ORs the groups. An empty group matches.
 * (Walks the raw rules rather than ruleGroupToRows, which would mint an unused
 * UUID per rule on every evaluation.)
 */
export const evaluateQuery = (
  contact: Contact,
  query: ContactFilterQuery | null | undefined,
): MatchResult => {
  const items = query?.rules;

  if (!items || items.length === 0) return "match";

  let overall: MatchResult | null = null; // OR across closed groups
  let group: MatchResult | null = null; // AND within the current group
  let combinator: "and" | "or" = "and";

  for (const item of items) {
    if (typeof item === "string") {
      combinator = item === "or" ? "or" : "and";
      continue;
    }
    // A nested group is one term of this list: the drawer parenthesises an
    // OR-shaped base before ANDing its facets on, so skipping groups here would
    // read that base as "no constraint" and match rows the server excludes.
    const ruleResult =
      "rules" in item
        ? evaluateQuery(contact, item as ContactFilterQuery)
        : evaluateRule(contact, item as ContactFilterRule);

    if (group === null) {
      group = ruleResult;
    } else if (combinator === "or") {
      overall = overall === null ? group : orResult(overall, group);
      group = ruleResult;
    } else {
      group = andResult(group, ruleResult);
    }
  }

  if (group === null) return "match";

  return overall === null ? group : orResult(overall, group);
};

/**
 * Whether a conversation's contact matches an inbox filter. The built-in axes
 * (assignment / status / view scoping / parked) are always derivable and yield
 * a confident no-match on failure; the advanced query contributes the tri-state.
 * Read Mentions membership and non-derivable participation remain cache-backed.
 */
/**
 * Whether a contact is provably a member of an opaque presence view from its own
 * derivable fields alone - independent of cache presence. The backend defines
 * `participating` as "assigned to me OR authored a message"; the assignment half
 * IS derivable, so a contact now assigned to the current user is definitively
 * participating. This is what lets an assign-to-me surface the row in the
 * Participating view before it has ever been cached there. A positive unread
 * mention count similarly proves Mentions membership; read mentions and the
 * authored-a-message half of participating still fall back to cache presence.
 */
export const isDerivablyInView = (
  contact: Contact,
  view: InboxFilter["view"],
  currentUserId: string | null,
): boolean => {
  if (view === CONVERSATION_VIEW.MENTIONS) {
    return contact.unreadMentionCount > 0;
  }

  return (
    view === CONVERSATION_VIEW.PARTICIPATING &&
    currentUserId != null &&
    contact.assignedUserId === currentUserId
  );
};

/**
 * Mirrors the server's assignee predicate so a live Pusher update buckets a
 * contact the same way a refetch would. "teammates" is owned-but-not-by-you:
 * unassigned work is excluded, matching `NULL <> uuid` being NULL server-side.
 */
export const matchesAssignee = (
  contact: Contact,
  assignedTo: InboxFilter["assignedTo"],
  currentUserId: string | null,
): boolean => {
  if (assignedTo === "all") return true;

  if (assignedTo === ASSIGNED_TO_TEAMMATES) {
    return (
      contact.assignedUserId != null && contact.assignedUserId !== currentUserId
    );
  }

  return contact.assignedUserId === assignedTo;
};

export const evaluateInboxFilter = (
  contact: Contact,
  filter: InboxFilter,
  currentUserId: string | null = null,
): MatchResult => {
  if (!matchesAssignee(contact, filter.assignedTo, currentUserId)) {
    return "no-match";
  }

  if (filter.status !== "all" && contact.status !== filter.status) {
    return "no-match";
  }

  if (filter.view === "unattended") {
    if (!contact.lastMessageId || contact.lastMessageOwner !== OWNER.CONTACT) {
      return "no-match";
    }
  }

  if (filter.view === CONVERSATION_VIEW.FOLLOW_UP && !contact.followUp) {
    return "no-match";
  }

  // AI view: Echo is on, sending or drafting, whoever else holds the
  // conversation - the assignee check above already narrowed by owner.
  if (filter.view === CONVERSATION_VIEW.AI && contact.aiMode === "off") {
    return "no-match";
  }

  if (isParkedFromDefaultView(contact, filter.view, filter.status)) {
    return "no-match";
  }

  return evaluateQuery(contact, filter.query);
};
